import { useState, useEffect, useRef, useCallback } from "react"
import CMSController from "aldoo-ra/CMS/controllers/cms-controller"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import LocalizationController from "aldoo-ra/Localization/localization-controller"
import LocalizationItemDialog from "aldoo-ra/Localization/localization-item-dialog"
import { GlobeAltIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline"

// Custom user fields live under `entry.params` (an object), keeping a hard
// boundary between the canonical PFV fields and arbitrary user data.

let _plansCache = null

const PRESET_OPTIONS = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
  { label: "Custom", value: "custom" },
]

function paramToPreset(param) {
  if (param === "✓") return [PRESET_OPTIONS[0]]
  if (param === "✗") return [PRESET_OPTIONS[1]]
  return [PRESET_OPTIONS[2]]
}

/**
 * Custom CMS field editor for ProductFeature plan_values.
 *
 * Data shape: [{ plan: "<ProductPlan _id>", param: "1 image only", value: "1" }, ...]
 *   param — display label (localized, shown on site)
 *   value — machine value used by systems (e.g. "5", "unlimited")
 */
export default function PlanValuesEditor({ onChange, value, readOnly = false, parentObject }) {
  const [allPlans, setAllPlans] = useState([])
  const [activePlanId, setActivePlanId] = useState(null)
  const [localValue, setLocalValue] = useState(() => (Array.isArray(value) ? value : []))
  const [showAddMenu, setShowAddMenu] = useState(false)
  const isInternal = useRef(false)
  const customDraftRef = useRef({}) // planId -> last custom text
  const { getContent } = CMSController()

  // Localization state
  const localizationController = LocalizationController()
  const [localeDialogOpen, setLocaleDialogOpen] = useState(false)
  const [localeItem, setLocaleItem] = useState(null)
  const [localeLoading, setLocaleLoading] = useState(false)
  const [localeLanguagesLoaded, setLocaleLanguagesLoaded] = useState(false)
  const [localeTargetPlanId, setLocaleTargetPlanId] = useState(null)

  useEffect(() => {
    if (_plansCache) {
      setAllPlans(_plansCache)
      return
    }
    getContent({
      match: { "content.type": "ProductPlan" },
      sort: { "content.sort_order": 1 },
      itemsPerPage: 50,
    }).then((res) => {
      if (!res?.data) return
      const sorted = res.data.sort((a, b) => (a.content?.sort_order || 0) - (b.content?.sort_order || 0))
      _plansCache = sorted
      setAllPlans(sorted)
    })
  }, [])

  useEffect(() => {
    if (isInternal.current) { isInternal.current = false; return }
    if (Array.isArray(value)) setLocalValue(value)
  }, [value])

  useEffect(() => {
    if (activePlanId) return
    const first = localValue[0] ? resolvePlanId(localValue[0].plan) : null
    if (first) setActivePlanId(first)
  }, [localValue])

  // Localization refs are now stored on standalone PlanFeatureValue docs and
  // managed by the caller, so this editor no longer auto-embeds anything.

  const update = useCallback((next) => {
    isInternal.current = true
    setLocalValue(next)
    onChange?.(next)
  }, [onChange])

  const resolvePlanId = (plan) => (typeof plan === "string" ? plan : plan?._id)

  const getEntry = (planId) => localValue.find((e) => resolvePlanId(e.plan) === planId)

  const handleAddPlan = (planId) => {
    if (localValue.find((e) => resolvePlanId(e.plan) === planId)) return
    update([...localValue, { plan: planId, param: "" }])
    setActivePlanId(planId)
    setShowAddMenu(false)
  }

  const handleRemovePlan = (planId) => {
    const next = localValue.filter((e) => resolvePlanId(e.plan) !== planId)
    update(next)
    if (activePlanId === planId) {
      setActivePlanId(next[0] ? resolvePlanId(next[0].plan) : null)
    }
  }

  const handleParamChange = (val) => {
    const exists = localValue.find((e) => resolvePlanId(e.plan) === activePlanId)
    const next = exists
      ? localValue.map((e) => (resolvePlanId(e.plan) === activePlanId ? { ...e, param: val } : e))
      : [...localValue, { plan: activePlanId, param: val, value: "" }]
    update(next)
  }

  const handleValueChange = (val) => {
    const exists = localValue.find((e) => resolvePlanId(e.plan) === activePlanId)
    const next = exists
      ? localValue.map((e) => (resolvePlanId(e.plan) === activePlanId ? { ...e, value: val } : e))
      : [...localValue, { plan: activePlanId, param: "", value: val }]
    update(next)
  }

  const getCustomFields = (entry) => {
    const obj = entry?.params || {}
    return Object.keys(obj).map((k) => ({ key: k, value: obj[k] }))
  }

  const setEntryParams = (planId, mutate) => {
    const next = localValue.map((e) => {
      if (resolvePlanId(e.plan) !== planId) return e
      const params = { ...(e.params || {}) }
      mutate(params)
      return { ...e, params }
    })
    update(next)
  }

  const handleAddCustomField = () => {
    const entry = getEntry(activePlanId)
    const existing = Object.keys(entry?.params || {})
    let name = "field"
    let i = 1
    while (existing.includes(name)) { name = `field${i}`; i++ }
    setEntryParams(activePlanId, (p) => { p[name] = "" })
  }

  const handleCustomFieldValueChange = (fieldKey, val) => {
    setEntryParams(activePlanId, (p) => { p[fieldKey] = val })
  }

  const handleCustomFieldRename = (oldKey, newKey) => {
    if (!newKey || newKey === oldKey) return
    setEntryParams(activePlanId, (p) => {
      p[newKey] = p[oldKey]
      delete p[oldKey]
    })
  }

  const handleRemoveCustomField = (fieldKey) => {
    setEntryParams(activePlanId, (p) => { delete p[fieldKey] })
  }

  const planName = (p) => p.content?.name || p.content?.id || p._id

  const addedPlanIds = new Set(localValue.map((e) => resolvePlanId(e.plan)))
  const addedPlans = allPlans.filter((p) => addedPlanIds.has(p._id))
  const availablePlans = allPlans.filter((p) => !addedPlanIds.has(p._id))
  const activeEntry = activePlanId ? getEntry(activePlanId) : null

  // Localization helpers
  const getFeatureId = () => {
    const parent = typeof parentObject === "function" ? parentObject() : parentObject
    return parent?._id
  }

  const getLocalizationKey = (planId) => {
    const entry = getEntry(planId)
    if (!entry?._id) return null
    return `PlanFeatureValue-param-${entry._id}`
  }

  const loadLocaleLanguages = async () => {
    if (localeLanguagesLoaded) return
    const stats = await localizationController.getLanguageStatistics()
    if (!stats?.languages) return
    localizationController.setLanguages(stats.languages)
    const primary = stats.languages.find((l) => l.primary)
    if (primary) localizationController.setPrimaryLanguage(primary.code)
    setLocaleLanguagesLoaded(true)
  }

  const openLocaleDialog = async (planId) => {
    const key = getLocalizationKey(planId)
    if (!key) return

    setLocaleLoading(true)
    setLocaleTargetPlanId(planId)
    try {
      await loadLocaleLanguages()

      let items = await localizationController.getLocalizationKey(key)
      if (!items || items.length === 0) {
        const entry = getEntry(planId)
        await localizationController.addLocalizationKey({
          key,
          type: "text",
          language: localizationController.primaryLanguage || "en",
          content: entry?.param || "",
        })
        items = await localizationController.getLocalizationKey(key)
      }

      if (items?.[0]) {
        setLocaleItem(items[0])
        setLocaleDialogOpen(true)
      }
    } catch (e) {
      console.error("Failed to open localization dialog", e)
    } finally {
      setLocaleLoading(false)
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Plan tabs */}
      <div className="flex flex-wrap items-center border-b border-gray-200 bg-gray-50">
        {addedPlans.map((p) => {
          const isActive = activePlanId === p._id
          const entry = getEntry(p._id)
          const hasValue = entry?.param !== "" && entry?.param != null
          return (
            <div
              key={p._id}
              className={`flex items-center border-b-2 transition ${
                isActive ? "border-blue-500 bg-white" : "border-transparent hover:bg-gray-100"
              }`}
            >
              <button
                onClick={() => setActivePlanId(p._id)}
                className={`px-3 py-2 text-sm font-medium ${
                  isActive ? "text-blue-600" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {planName(p)}
                {hasValue && <span className="ml-1.5 w-2 h-2 rounded-full bg-green-400 inline-block" />}
              </button>
              {!readOnly && (
                <button
                  onClick={() => handleRemovePlan(p._id)}
                  className="pr-2 text-gray-300 hover:text-red-400 text-base leading-none"
                  title="Remove plan"
                >
                  ×
                </button>
              )}
            </div>
          )
        })}

        {!readOnly && availablePlans.length > 0 && (
          <div className="relative ml-1">
            <button
              onClick={() => setShowAddMenu((v) => !v)}
              className="px-3 py-2 text-sm font-medium text-blue-500 hover:text-blue-700"
            >
              + Add plan
            </button>
            {showAddMenu && (
              <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[160px]">
                {availablePlans.map((p) => (
                  <button
                    key={p._id}
                    onClick={() => handleAddPlan(p._id)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {planName(p)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {addedPlans.length === 0 && (
          <span className="px-4 py-2 text-sm text-gray-400">No plans added yet</span>
        )}
      </div>

      {/* Value inputs for active plan */}
      <div className="p-4 space-y-2">
        {activePlanId && addedPlanIds.has(activePlanId) ? (
          <>
            <div className="flex gap-2 items-center">
              <DropDownSelect
                options={PRESET_OPTIONS}
                value={paramToPreset(activeEntry?.param ?? "")}
                valueField="value"
                placeholder="Type"
                multiSelection={false}
                showCheckbox={false}
                showSelectAll={false}
                enableResetButton={false}
                fixedWidth={100}
                onChange={(selected) => {
                  if (readOnly) return
                  const v = selected?.[0]?.value
                  const current = activeEntry?.param ?? ""
                  if (v === "yes") {
                    if (current !== "✓" && current !== "✗") customDraftRef.current[activePlanId] = current
                    handleParamChange("✓")
                  } else if (v === "no") {
                    if (current !== "✓" && current !== "✗") customDraftRef.current[activePlanId] = current
                    handleParamChange("✗")
                  } else if (v === "custom") {
                    handleParamChange(customDraftRef.current[activePlanId] ?? "")
                  }
                }}
              />
              <input
                value={activeEntry?.param ?? ""}
                readOnly={readOnly}
                onChange={(e) => !readOnly && handleParamChange(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="Label (e.g. 1 image only, Unlimited)"
              />
              {!readOnly && getFeatureId() && (
                <button
                  onClick={() => openLocaleDialog(activePlanId)}
                  disabled={localeLoading && localeTargetPlanId === activePlanId}
                  className="p-1.5 text-gray-400 hover:text-blue-500 transition disabled:opacity-50"
                  title="Edit translations"
                >
                  <GlobeAltIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-xs text-gray-400 w-[100px] flex-shrink-0 text-right">value</span>
              <input
                value={activeEntry?.value ?? ""}
                readOnly={readOnly}
                onChange={(e) => !readOnly && handleValueChange(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                placeholder="Machine value (e.g. 5, unlimited)"
              />
            </div>

            {/* Custom fields */}
            {getCustomFields(activeEntry).map(({ key, value: fVal }) => (
              <div key={key} className="flex gap-2 items-center">
                <input
                  defaultValue={key}
                  readOnly={readOnly}
                  onBlur={(e) => !readOnly && handleCustomFieldRename(key, e.target.value.trim())}
                  className="w-[100px] flex-shrink-0 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 text-gray-500"
                  placeholder="field name"
                />
                <input
                  value={fVal ?? ""}
                  readOnly={readOnly}
                  onChange={(e) => !readOnly && handleCustomFieldValueChange(key, e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                  placeholder="value"
                />
                {!readOnly && (
                  <button
                    onClick={() => handleRemoveCustomField(key)}
                    className="p-1.5 text-gray-300 hover:text-red-400 transition"
                    title="Remove field"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}

            {!readOnly && (
              <button
                onClick={handleAddCustomField}
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 transition mt-1"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add field
              </button>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-400">Select a plan tab to edit its value.</p>
        )}
      </div>

      {/* Overview */}
      {addedPlans.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Overview</p>
          <div className="flex gap-4 flex-wrap">
            {addedPlans.map((p) => {
              const entry = getEntry(p._id)
              return (
                <div key={p._id} className="text-sm cursor-pointer" onClick={() => setActivePlanId(p._id)}>
                  <span className="text-gray-400">{planName(p)}:</span>{" "}
                  <span className="font-medium text-gray-700">{entry?.param || "—"}</span>
                  {entry?.value != null && entry.value !== "" && (
                    <span className="ml-1 text-xs text-gray-400">({entry.value})</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Localization dialog */}
      {localeDialogOpen && localeItem && (
        <LocalizationItemDialog
          isOpen={localeDialogOpen}
          onClose={() => {
            setLocaleDialogOpen(false)
            setLocaleItem(null)
            setLocaleTargetPlanId(null)
          }}
          onSave={(updatedItem) => {
            setLocaleItem(updatedItem)
          }}
          item={localeItem}
          controller={localizationController}
          parentObject={null}
        />
      )}
    </div>
  )
}
